import { useState, useCallback, useMemo, useEffect } from "react";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ValidatedFormValueService, MutableFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged } from "../../../framework/utils";
import { none, where } from "../../../framework/utils/collection";
import { TimeBlockEvents } from "../events";
import { ITimeBlocksCreatorFormModel, ITimeBlocksCreatorValueModel, ITimeBlocksCreatorModelChange } from "./blocks-creator-iface";
import { ICreateTimeBlocksRequest } from "../../../entities-generated/models/time-block/create-time-blocks-request";

// ##BEGIN:DEVELOPERCODE##

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:date/validated/number | null | undefined/number%%
export const date = new (class dateService extends ValidatedFormValueService<{}, number | null | undefined, number> {
    public initialize(data: {}): void {
        this.setInputValue(new BwjInput<number | null | undefined>(null));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: {}): void {}

    public getDefaultValue(): BwjInput<number | null | undefined> {
        return new BwjInput<number | null | undefined>(null);
    }

    public getInputValue(): BwjInput<number | null | undefined> {
        return this.value;
    }

    public setInputValue(value: BwjInput<number | null | undefined>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number {
        const value = this.value.value;
        return value ?? 0;
    }

    public validate(value: number | null | undefined): Promise<BwjInput<number | null | undefined>> {
        const result = this.requiredFieldValidator(value, 'Select the begin date');
        return Promise.resolve(result);
    }

    public onUpdate(value: number | null | undefined): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'date' // id
    ,'Time block begin date' // description
);
// %%END:date/validated/number | null | undefined/number%%

// %%BEGIN:numBlocks/validated/string/number%%
export const numBlocks = new (class numBlocksService extends ValidatedFormValueService<{}, string, number> {
    public initialize(data: {}): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: {}): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number {
        const value = this.value.value;
        const nb = parseInt(value);
        return isNaN(nb) === false ? nb : 1;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter the number of blocks to create');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'numBlocks' // id
    ,'Number of blocks to generate (24/day)' // description
);
// %%END:numBlocks/validated/string/number%%

// %%BEGIN:isBillableTime/mutable/boolean/boolean%%
export const isBillableTime = new (class isBillableTimeService extends MutableFormValueService<{}, boolean, boolean> {
    public initialize(data: {}): void {
        this.value = true;
    }

    public applyToDataModel(data: {}): void {}

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public setInputValue(value: boolean): void {
        this.value = value;
    }

    public getOutputValue(): boolean {
        return this.value;
    }

    public onUpdate(value: boolean): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'isBillableTime' // id
    ,'Intended for billable work' // description
);
// %%END:isBillableTime/mutable/boolean/boolean%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): ITimeBlocksCreatorFormModel => {
    return {
       date: date.getInputValue(),
       numBlocks: numBlocks.getInputValue(),
       isBillableTime: isBillableTime.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): ITimeBlocksCreatorValueModel => {
    return {
       date: date.getOutputValue(),
       numBlocks: numBlocks.getOutputValue(),
       isBillableTime: isBillableTime.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        date.getInputValue(),
        numBlocks.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: ITimeBlocksCreatorModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(TimeBlockEvents.CreateFormModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<ITimeBlocksCreatorFormModel>> => {
    const promises = [
        date.formValidateAndUpdate(),
        numBlocks.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:ITimeBlocksCreatorFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: ITimeBlocksCreatorValueModel, dataModel: {} } = {
    dataModel: {}
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: {}) => {
    const dataModel = data || {};

    date.initialize(dataModel);
    numBlocks.initialize(dataModel);
    isBillableTime.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:ITimeBlocksCreatorFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
date.setChangePublisher(broadcastChange);
numBlocks.setChangePublisher(broadcastChange);
isBillableTime.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = {};

    date.applyToDataModel(dataModel);
    numBlocks.applyToDataModel(dataModel);
    isBillableTime.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: ITimeBlocksCreatorModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], TimeBlockEvents.CreateFormModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): ITimeBlocksCreatorFormModel => {
    const [getter, setter] = useState<ITimeBlocksCreatorFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: ITimeBlocksCreatorModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: ITimeBlocksCreatorFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('TimeBlocksCreatorFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('TimeBlocksCreatorFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toCreateRequest = (): ICreateTimeBlocksRequest => toValueModel();

// ##END:DEVELOPERCODE##
